import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Card, CardBody, CardHeader, Typography } from '@la/ds-ui-components';
import {
  AddOnType,
  FileUploadFormField,
  Product,
  UserSelection,
} from '@la/types';
import {
  formatAsUSD,
  getProductPropertyLabel,
  isStaffRegistration,
} from '@la/utilities';
import {
  RegistrantInfoPayload,
  useRegistration,
} from 'lib/context/RegistrationContext/RegistrationContext';
import {
  makeSelectAddOnProductById,
  selectSelectedProducts,
} from 'redux/selectors/registrationAddOnsSelectors';
import { ProductWithMappedSelections } from '../MemberRegistration.types';
import { RegistrationInfoCard } from '../RegistrationInfoCard/RegistrationInfoCard';
import styles from './RegistrationReviewCard.module.scss';
import * as S from './RegistrationReviewCard.styles';

export const RegistrationReviewCard = () => {
  const [firstRender, setFirstRender] = useState(true);
  const {
    ctx,
    fileUploadFormFields,
    formFields,
    ngbMembershipType,
    setHasErrors,
    showNGBVerification,
    signedWaivers,
    waivers,
  } = useRegistration();

  const selectedProducts = useSelector(selectSelectedProducts);

  useEffect(() => {
    if (firstRender) {
      setHasErrors(false);
      setFirstRender(false);
    }
  }, [firstRender, setHasErrors]);

  const renderFieldsCard = () => {
    // We will always show the info card.
    // If it is a staff registration and there are no fields we show the user's name.
    // In case of a player registration with no fields we show the player's name.
    const allowEdit =
      !isStaffRegistration(ctx?.type) ||
      (formFields && formFields.nonFileUploadFormFields?.length > 0) ||
      (showNGBVerification && ngbMembershipType);

    return <RegistrationInfoCard allowEditMode={Boolean(allowEdit)} />;
  };

  const renderUploadDocumentsCard = () => {
    const filenames = Object.values(fileUploadFormFields);

    if (!filenames.length) {
      return null;
    }

    return (
      <Card>
        <CardHeader>Uploaded Documents</CardHeader>
        <CardBody>
          <Files fileUploadFormFields={filenames} />
        </CardBody>
      </Card>
    );
  };

  return (
    <div className={styles.registrationReviewContainer}>
      {renderFieldsCard()}
      {renderUploadDocumentsCard()}
      <Card>
        <CardHeader>Signed Documents</CardHeader>
        <CardBody>
          <div className={styles.entriesContainer}>
            <Waivers waivers={waivers} signedWaivers={signedWaivers} />
          </div>
        </CardBody>
      </Card>
      {selectedProducts.length > 0 ? (
        <AddOnSelectionsCard selectedProducts={selectedProducts} />
      ) : null}
    </div>
  );
};

const Files = ({
  fileUploadFormFields,
}: {
  fileUploadFormFields: FileUploadFormField[];
}) => {
  return (
    <>
      {fileUploadFormFields.map((fileUploadFormField, i) => {
        return (
          <div className={styles.waiversContainer} key={i}>
            <Typography variant="ui" size="large">
              {fileUploadFormField.name}:
            </Typography>
            <Typography variant="ui" size="large" weight="bold">
              {fileUploadFormField.value?.name}
            </Typography>
          </div>
        );
      })}
    </>
  );
};

const Waivers = ({
  waivers,
  signedWaivers,
}: {
  waivers: RegistrantInfoPayload['waivers'] | null;
  signedWaivers: Record<string, boolean>;
}) => {
  if (!waivers?.length) {
    return null;
  }

  return (
    <>
      {waivers.map((waiver, i) => {
        const isSigned = signedWaivers[waiver.waiverId];

        return (
          <div className={styles.waiversContainer} key={waiver.waiverId ?? i}>
            <Typography variant="ui" size="large">
              {waiver.name}:
            </Typography>
            <Typography variant="ui" size="large" weight="bold">
              {isSigned ? 'Signed' : 'Not signed'}
            </Typography>
          </div>
        );
      })}
    </>
  );
};

export function AddOnSelectionsCard({
  selectedProducts,
}: Readonly<{
  selectedProducts: ProductWithMappedSelections[];
}>) {
  return (
    <Card>
      <CardHeader>Selected Add-ons</CardHeader>
      <CardBody>
        <div className={styles.entriesContainer}>
          <AddOnSelectionEntries selectedProducts={selectedProducts} />
        </div>
      </CardBody>
    </Card>
  );
}

export function AddOnSelectionEntries({
  selectedProducts,
}: Readonly<{ selectedProducts: ProductWithMappedSelections[] }>) {
  return (
    <>
      {selectedProducts.map(
        (
          {
            addOnType,
            productId,
            productName,
            quantity,
            salesTax,
            selectionTypes,
            subTotal,
          },
          idx
        ) => {
          return (
            <AddOnSelectionEntry
              addOnType={addOnType}
              key={`${productId}-${idx}`}
              productId={productId}
              productName={productName}
              quantity={quantity}
              salesTax={salesTax}
              selectionTypes={selectionTypes}
              subTotal={subTotal}
            />
          );
        }
      )}
    </>
  );
}

function AddOnSelectionEntry({
  addOnType,
  productId,
  productName,
  quantity,
  salesTax,
  selectionTypes,
  subTotal,
}: Readonly<{
  addOnType: AddOnType;
  productId: number;
  productName: string;
  quantity: number;
  salesTax: number;
  selectionTypes: UserSelection[];
  subTotal: number;
}>) {
  const selectAddOnProductById = useMemo(
    () => makeSelectAddOnProductById(productId),
    [productId]
  );
  const addOnProduct = useSelector(selectAddOnProductById);

  if (!addOnProduct) {
    return null;
  }

  const propertiesDescription = buildPropertiesDescription(
    addOnProduct,
    quantity,
    selectionTypes
  );
  const priceDisplay = calculateDisplayPrice(addOnType, salesTax, subTotal);

  return (
    <div className={styles.addOnEntry}>
      <div className={styles.fieldEntry}>
        <Typography size="large" variant="ui">
          {productName}
        </Typography>
        <Typography size="large" variant="ui" weight="bold">
          {priceDisplay}
        </Typography>
      </div>
      <S.AddOnPropertiesDesc>{propertiesDescription}</S.AddOnPropertiesDesc>
    </div>
  );
}

function buildPropertiesDescription(
  addOnProduct: Product,
  quantity: number,
  selectionTypes: ProductWithMappedSelections['selectionTypes']
): string {
  let description = `Quantity: ${quantity}`;

  if (!addOnProduct?.properties) {
    return description;
  }

  for (const selection of selectionTypes) {
    const { optionId, selectedValue } = selection;
    const property = addOnProduct.properties.find((prop) => {
      return prop.id === Number(optionId);
    });

    if (property) {
      const value = getProductPropertyLabel(selectedValue, property);

      description = description.concat(`, ${property.name}: ${value}`);
    }
  }

  return description;
}

export function calculateDisplayPrice(
  addOnType: AddOnType,
  salesTax: number,
  subTotal: number
): string {
  if (subTotal === 0) {
    return addOnType === 'required' ? 'Included' : 'Free';
  }

  return formatAsUSD(subTotal + salesTax);
}
